import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";
import { readMonthlySaleReportParams, monthlySaleReportQuery } from "../utils/monthlySaleReportNav";
import {
  loadExpenseRowsForFinancialYear,
  MONTH_LABELS,
} from "../features/expenseDataImport/services/expenseReportService";
import {
  PARTY_SPARE_WAY_AUTO_LINK,
  SPARE_WAY_RULES,
  buildSpareWayFySummary,
  buildSpareWayMonthDetail,
} from "../features/expenseDataImport/services/spareWayAutoLinkService";
import { spareWayAutoLinkOpeningBalance } from "../features/expenseDataImport/constants";
import "./MonthlySaleReportPage.css";
import "./ExpenseBranchTransferPage.css";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function netAmountClass(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.01) return "ebt-net-zero";
  return v > 0 ? "ebt-net-positive" : "ebt-net-negative";
}

export default function ExpenseSpareWayReportPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readMonthlySaleReportParams(searchParams, now);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState("");

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const periodLabel = `${MONTH_LABELS[monthIndex]} ${year}`;

  useEffect(() => {
    const q = new URLSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, "Main"));
    setSearchParams(q.toString(), { replace: true });
  }, [fyStartYear, fySlot, setSearchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setProgress("Loading expense data…");
    try {
      const data = await loadExpenseRowsForFinancialYear(fyStartYear, (done, total) => {
        setProgress(`Loading month ${done} / ${total}…`);
      });
      setRows(data);
    } catch (e) {
      toast.error(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
      setProgress("");
    }
  }, [fyStartYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fySummary = useMemo(() => buildSpareWayFySummary(rows), [rows]);

  const openingBalance = spareWayAutoLinkOpeningBalance(fyStartYear);

  const monthDetail = useMemo(
    () => buildSpareWayMonthDetail(rows, { calendarYear: year, monthIndex }),
    [rows, year, monthIndex]
  );

  // Running balance per FY slot: opening balance + cumulative net.
  const runningBalances = useMemo(() => {
    let bal = openingBalance;
    const bySlot = new Map();
    for (let slot = 0; slot < FY_MONTH_ORDER_SHORT.length; slot++) {
      const m = fySummary.months.find((x) => x.fySlot === slot);
      if (m) bal += m.net;
      bySlot.set(slot, bal);
    }
    return bySlot;
  }, [fySummary, openingBalance]);

  const closingBalance = openingBalance + fySummary.totals.net;

  return (
    <>
      <div className="msr-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <main className="msr-upload-page ebt-page msr-no-print">
        <header className="msr-upload-page-head">
          <div>
            <p className="msr-upload-page-kicker">Reports · Branch finance</p>
            <h1 className="msr-upload-page-title">Expense — Spare Way Auto Link</h1>
            <p className="msr-upload-page-sub">
              {PARTY_SPARE_WAY_AUTO_LINK} (branch of Main Branch) — Main Branch (ID 1) only. Credit Sales
              (Dr), Inward Supply (Cr), Receipt (Cr), and Payment (Dr) from uploaded expense vouchers.
            </p>
          </div>
          <Link to="/reports/expense-upload" className="msr-upload-page-back">
            Expense upload →
          </Link>
        </header>

        <div className="ebt-rules-grid ebt-rules-grid-compact">
          {SPARE_WAY_RULES.map((rule) => (
            <div key={rule.id} className="ebt-rule-card">
              <div className="ebt-rule-title">{rule.title}</div>
              <div className="ebt-rule-row">
                <span>Voucher</span>
                <strong>{rule.voucherType}</strong>
              </div>
              <div className="ebt-rule-row">
                <span>Party</span>
                <strong className="ebt-party-fixed">{rule.party}</strong>
              </div>
              <div className="ebt-rule-row">
                <span>Note</span>
                <span>{rule.branch}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="msr-toolbar msr-upload-toolbar">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select
              className="msr-toolbar-select"
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
            >
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="msr-print-btn" onClick={loadData} disabled={loading}>
            Refresh
          </button>
          <button type="button" className="msr-print-btn" onClick={() => window.print()} disabled={loading}>
            Print A4
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-6">{progress || "Loading…"}</p>
        ) : (
          <>
            <div className="ebt-dashboard">
              <div className="ebt-card ebt-card-out">
                <span>Credit Sales (Dr)</span>
                <strong>{fmt(monthDetail.summary.creditSalesTotal)}</strong>
                <em>{periodLabel}</em>
              </div>
              <div className="ebt-card ebt-card-out">
                <span>Payment (Dr)</span>
                <strong>{fmt(monthDetail.summary.paymentTotal)}</strong>
                <em>{periodLabel}</em>
              </div>
              <div className="ebt-card ebt-card-in">
                <span>Credit Note (Cr)</span>
                <strong>{fmt(monthDetail.summary.creditNoteTotal)}</strong>
                <em>{periodLabel}</em>
              </div>
              <div className="ebt-card ebt-card-in">
                <span>Inward Supply (Cr)</span>
                <strong>{fmt(monthDetail.summary.inwardSupplyTotal)}</strong>
                <em>
                  {monthDetail.summary.debitNoteTotal > 0
                    ? `Inward ${fmt(monthDetail.summary.inwardSupplyGross)} − Debit Note ${fmt(monthDetail.summary.debitNoteTotal)}`
                    : periodLabel}
                </em>
              </div>
              <div className="ebt-card ebt-card-rcpt">
                <span>Receipt (Cr) + Journal credit</span>
                <strong>{fmt(monthDetail.summary.receiptCombinedTotal)}</strong>
                <em>
                  Receipt {fmt(monthDetail.summary.receiptTotal)} + Journal{" "}
                  {fmt(monthDetail.summary.journalCreditTotal)}
                </em>
              </div>
              <div className="ebt-card ebt-card-pending">
                <span>Net (Dr − Cr)</span>
                <strong className={netAmountClass(monthDetail.summary.netTotal)}>
                  {fmt(monthDetail.summary.netTotal)}
                </strong>
                <em>
                  (Credit Sales + Payment) − (Credit Note + Inward Supply − Debit Note + Receipt + Journal
                  credit)
                </em>
              </div>
            </div>

            <section className="ebt-section">
              <h2 className="ebt-section-title">Month-wise summary — FY {fyLabel}</h2>
              <p className="ebt-section-sub">
                Party: {PARTY_SPARE_WAY_AUTO_LINK} · Main Branch (ID 1) only · Opening balance (FY {fyLabel}
                ): {fmt(openingBalance)} · Click a month row to update the dashboard cards above.
              </p>
              <div className="msr-precalc-table-wrap">
                <table className="msr-precalc-table ebt-table ebt-spareway-summary-table">
                  <colgroup>
                    <col className="ebt-sw-col-month" />
                    <col className="ebt-sw-col-num" span={6} />
                    <col className="ebt-sw-col-net" />
                    <col className="ebt-sw-col-balance" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="msr-num">Credit Sales (Dr)</th>
                      <th className="msr-num">Payment (Dr)</th>
                      <th className="msr-num">Credit Note (Cr)</th>
                      <th className="msr-num">Inward Supply (Cr)</th>
                      <th className="msr-num">Receipt (Cr) + Journal credit</th>
                      <th className="msr-num">Net</th>
                      <th className="msr-num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-semibold">Opening balance</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num">—</td>
                      <td className="msr-num font-semibold">{fmt(openingBalance)}</td>
                    </tr>
                    {FY_MONTH_ORDER_SHORT.map((label, slot) => {
                      const m = fySummary.months.find((x) => x.fySlot === slot);
                      const active = slot === fySlot;
                      return (
                        <tr
                          key={label}
                          className={active ? "ebt-row-active" : ""}
                          onClick={() => m && setFySlot(slot)}
                          style={m ? { cursor: "pointer" } : undefined}
                          title={m ? "Click to select this month" : undefined}
                        >
                          <td>
                            {label}
                            {m?.calendarYear ? ` ${m.calendarYear}` : ""}
                          </td>
                          <td className="msr-num">{m ? fmt(m.creditSales) : "—"}</td>
                          <td className="msr-num">{m ? fmt(m.payment) : "—"}</td>
                          <td className="msr-num">{m ? fmt(m.creditNote) : "—"}</td>
                          <td className="msr-num">{m ? fmt(m.inwardSupplyNet) : "—"}</td>
                          <td className="msr-num">{m ? fmt(m.receiptCombined) : "—"}</td>
                          <td className={`msr-num ${m ? netAmountClass(m.net) : ""}`}>
                            {m ? fmt(m.net) : "—"}
                          </td>
                          <td className="msr-num">{m ? fmt(runningBalances.get(slot)) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="font-semibold">FY total</td>
                      <td className="msr-num font-semibold">{fmt(fySummary.totals.creditSales)}</td>
                      <td className="msr-num font-semibold">{fmt(fySummary.totals.payment)}</td>
                      <td className="msr-num font-semibold">{fmt(fySummary.totals.creditNote)}</td>
                      <td className="msr-num font-semibold">{fmt(fySummary.totals.inwardSupplyNet)}</td>
                      <td className="msr-num font-semibold">{fmt(fySummary.totals.receiptCombined)}</td>
                      <td className={`msr-num font-semibold ${netAmountClass(fySummary.totals.net)}`}>
                        {fmt(fySummary.totals.net)}
                      </td>
                      <td className="msr-num font-semibold">{fmt(closingBalance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          </>
        )}
      </main>

      <section className="ebt-print-sheet">
        <header className="ebt-print-head">
          <div className="ebt-print-company">WINS AUTOS</div>
          <h1 className="ebt-print-title">Spare Way Auto Link — Month-wise Summary</h1>
          <div className="ebt-print-meta">
            <span>
              Financial year: <strong>{fyLabel}</strong>
            </span>
            <span>
              Party: <strong>{PARTY_SPARE_WAY_AUTO_LINK}</strong>
            </span>
            <span>
              Branch: <strong>Main Branch (ID 1)</strong>
            </span>
            <span>
              Opening balance: <strong>{fmt(openingBalance)}</strong>
            </span>
            <span>Printed: {new Date().toLocaleString("en-IN")}</span>
          </div>
        </header>

        <table className="ebt-print-table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="msr-num">Credit Sales (Dr)</th>
              <th className="msr-num">Payment (Dr)</th>
              <th className="msr-num">Credit Note (Cr)</th>
              <th className="msr-num">Inward Supply (Cr)</th>
              <th className="msr-num">Receipt (Cr) + Journal credit</th>
              <th className="msr-num">Net</th>
              <th className="msr-num">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="ebt-print-opening">
              <td>Opening balance</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">{fmt(openingBalance)}</td>
            </tr>
            {FY_MONTH_ORDER_SHORT.map((label, slot) => {
              const m = fySummary.months.find((x) => x.fySlot === slot);
              return (
                <tr key={label}>
                  <td>
                    {label}
                    {m?.calendarYear ? ` ${m.calendarYear}` : ""}
                  </td>
                  <td className="msr-num">{m ? fmt(m.creditSales) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.payment) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.creditNote) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.inwardSupplyNet) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.receiptCombined) : "—"}</td>
                  <td className={`msr-num ${m ? netAmountClass(m.net) : ""}`}>{m ? fmt(m.net) : "—"}</td>
                  <td className="msr-num">{m ? fmt(runningBalances.get(slot)) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>FY total</td>
              <td className="msr-num">{fmt(fySummary.totals.creditSales)}</td>
              <td className="msr-num">{fmt(fySummary.totals.payment)}</td>
              <td className="msr-num">{fmt(fySummary.totals.creditNote)}</td>
              <td className="msr-num">{fmt(fySummary.totals.inwardSupplyNet)}</td>
              <td className="msr-num">{fmt(fySummary.totals.receiptCombined)}</td>
              <td className={`msr-num ${netAmountClass(fySummary.totals.net)}`}>
                {fmt(fySummary.totals.net)}
              </td>
              <td className="msr-num">{fmt(closingBalance)}</td>
            </tr>
          </tfoot>
        </table>

        <footer className="ebt-print-foot">
          <span>Debit = Credit Sales + Payment · Credit = Credit Note + (Inward Supply − Debit Note) + Receipt + Journal credit · Main Branch (ID 1) only</span>
          <span>
            Inward Supply (Cr) column = Inward Supply − Debit Note · Balance = Opening + cumulative Net
          </span>
        </footer>
      </section>
    </>
  );
}
